import { Controller } from "@hotwired/stimulus";
import TomSelect from "tom-select";
import { bindTomSelectClipboardSync } from "../legacy/search";

const treeCache = new Map();

export default class extends Controller {
  static values = {
    url: String,
    multiselect: { type: Boolean, default: false },
    rootsOnly: { type: Boolean, default: false },
  };

  connect() {
    // Capture initial selected values from the DOM before TomSelect replaces the element
    const initialValues = Array.from(this.element.selectedOptions)
      .map((o) => o.value)
      .filter((v) => v !== "");

    if (!treeCache.has(this.urlValue)) {
      treeCache.set(this.urlValue, fetch(this.urlValue).then((res) => res.json()));
    }
    treeCache.get(this.urlValue).then((tree) => {
      const { options, byCode, childrenOf } = this.buildFlatOptions(tree);
      this.initTomSelect(options, byCode, childrenOf, initialValues);
    });
  }

  disconnect() {
    if (this.tomSelect) {
      this.tomSelect.destroy();
      this.tomSelect = null;
    }
  }

  // Flatten the nested tree into a list of options, tracking depth and parent relationships.
  buildFlatOptions(tree) {
    const options = [];
    const byCode = {};
    const childrenOf = {};

    const walk = (nodes, depth, parentCode, rootCode) => {
      nodes.forEach((node) => {
        const myRootCode = depth === 0 ? node.code : rootCode;
        const opt = {
          code: node.code,
          name: node.name,
          depth,
          parentCode,
          rootCode: myRootCode,
        };
        options.push(opt);
        byCode[node.code] = opt;
        childrenOf[node.code] = childrenOf[node.code] || [];
        if (parentCode) {
          childrenOf[parentCode] = childrenOf[parentCode] || [];
          childrenOf[parentCode].push(node.code);
        }
        if (node.children && node.children.length) {
          walk(node.children, depth + 1, node.code, myRootCode);
        }
      });
    };

    walk(tree, 0, null, null);
    return { options, byCode, childrenOf };
  }

  // Return all descendant codes (children + grandchildren) for a given code.
  allDescendants(code, childrenOf) {
    const result = [];
    const queue = [...(childrenOf[code] || [])];
    while (queue.length) {
      const c = queue.shift();
      result.push(c);
      (childrenOf[c] || []).forEach((child) => queue.push(child));
    }
    return result;
  }

  // Compute which option codes should be visible for a given query.
  // Returns null to show all (empty query), or a Set of visible codes.
  computeVisible(query, options, byCode, childrenOf) {
    if (!query || query.trim() === "") return null;

    const q = query.toLowerCase();
    const visible = new Set();

    options.forEach((opt) => {
      const matches =
        opt.name.toLowerCase().includes(q) ||
        opt.code.toLowerCase().includes(q);

      if (!matches) return;

      if (opt.depth === 0) {
        // Root match: show it and all descendants
        visible.add(opt.code);
        this.allDescendants(opt.code, childrenOf).forEach((c) =>
          visible.add(c)
        );
      } else {
        // Child/grandchild match: show it and its full ancestor chain
        visible.add(opt.code);
        let cur = opt;
        while (cur.parentCode) {
          visible.add(cur.parentCode);
          cur = byCode[cur.parentCode];
        }
      }
    });

    return visible;
  }

  renderSetEntry(item, escape) {
    const code = item.code.toLowerCase();
    const unsupported = window.__keyruneUnsupportedSvgMap || {};
    let icon;
    if (unsupported[code]) {
      icon = `<span class="set-symbol set-symbol-svg" style="mask-image: url(${escape(unsupported[code])}); -webkit-mask-image: url(${escape(unsupported[code])});" title="${escape(item.name)}"></span>`;
    } else if (item.image_symbol_svg_url && unsupported[code] !== undefined) {
      icon = `<span class="set-symbol set-symbol-svg" style="mask-image: url(${escape(item.image_symbol_svg_url)}); -webkit-mask-image: url(${escape(item.image_symbol_svg_url)});" title="${escape(item.name)}"></span>`;
    } else {
      icon = `<i class="ss ss-${escape(code)}"></i>`;
    }
    return `<span style="display:inline-flex; align-items:center; gap:6px;"><span style="display:inline-flex; align-items:center;">${icon}</span><span>${escape(item.name)}</span><span class="text-muted">${escape(item.code)}</span></span>`;
  }

  initTomSelect(options, byCode, childrenOf, initialValues) {
    const self = this;
    const plugins = this.multiselectValue ? ["remove_button"] : ["clear_button"];

    const filteredOptions = this.rootsOnlyValue
      ? options.filter((o) => o.depth === 0)
      : options;

    this.tomSelect = new TomSelect(this.element, {
      options: filteredOptions,
      valueField: "code",
      labelField: "name",
      searchField: ["name", "code"],
      maxItems: this.multiselectValue ? null : 1,
      maxOptions: 2000,
      items: initialValues,
      plugins,
      placeholder: "Select a set...",

      score: function (search) {
        if (self.rootsOnlyValue) {
          // Only root sets; use simple substring match
          const q = search.toLowerCase();
          return function (item) {
            if (!q) return 1;
            return item.name.toLowerCase().includes(q) ||
              item.code.toLowerCase().includes(q)
              ? 1
              : 0;
          };
        }

        const visible = self.computeVisible(
          search,
          filteredOptions,
          byCode,
          childrenOf
        );
        return function (item) {
          if (visible === null) return 1;
          return visible.has(item.code) ? 1 : 0;
        };
      },

      render: {
        option: function (item, escape) {
          const prefix = item.depth > 0 ? "↳ " : "";
          return `<div class="set-tree-option depth-${item.depth}" style="padding-left:${8 + item.depth * 14}px">${prefix}${self.renderSetEntry(item, escape)}</div>`;
        },
        item: function (item, escape) {
          return `<div>${self.renderSetEntry(item, escape)}</div>`;
        },
      },
    });
    // bootstrap_form adds form-select to the <select>; TomSelect copies it to
    // ts-wrapper which causes a double border — remove it.
    this.tomSelect.wrapper.classList.remove("form-select");
    bindTomSelectClipboardSync(this.element);
  }
}
